'use client';

import { useEffect, useMemo, useState } from 'react';
import Script from 'next/script';
import { Check, Code, Copy } from 'lucide-react';
import { InfoModal } from '@/components/ui/InfoModal/InfoModal';
import { Button } from '@/components/ui/Button/Button';
import { toast } from '@/components/ui/Toasts/use-toast';
import { analytics } from '@/lib/analytics';
import { copyToClipboard } from '@/utils/clipboard';
import {
  DEFAULT_EMBED_LAYOUT,
  DEFAULT_EMBED_THEME,
  EMBED_LAYOUTS,
  EMBED_THEMES,
  type EmbedLayout,
  type EmbedTheme,
} from '@/utils/embed/params';
import { buildEmbedSnippet, EMBED_FALLBACK_HEIGHT, embedPlayerUrl } from '@/utils/embed/snippet';

interface Props {
  open: boolean;
  onClose: () => void;
  tone: { id?: number | null; title?: string | null };
}

const LAYOUT_LABEL: Record<EmbedLayout, { label: string; hint: string }> = {
  full: { label: 'Full', hint: 'Header, player and controls' },
  compact: { label: 'Compact', hint: 'Header and a bar that opens up' },
};

const THEME_LABEL: Record<EmbedTheme, { label: string; hint: string }> = {
  dark: { label: 'Dark', hint: 'For dark pages' },
  light: { label: 'Light', hint: 'For light pages' },
};

/** Segmented pill control; one per option group. */
function Pills<T extends string>({
  label,
  options,
  labels,
  value,
  onChange,
}: {
  label: string;
  options: readonly T[];
  labels: Record<T, { label: string; hint: string }>;
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="inline-flex self-start items-center h-[46px] p-1.5 rounded-full bg-zinc-800"
    >
      {options.map((option) => {
        const selected = value === option;
        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option)}
            title={labels[option].hint}
            className={`px-4 h-full rounded-full text-sm font-semibold transition-colors duration-150 ${
              selected ? 'bg-white text-black' : 'text-zinc-400 hover:text-white'
            }`}
          >
            {labels[option].label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Share → Embed: a live preview of the frame other sites will show, the
 * layout / theme options, and a button that copies the snippet. "Plug in and
 * play" is always on in shared embeds for now (the route still honours
 * `live=0` for hosts that want it off).
 * The preview is the real /embed/player page sized by the real /embed.js, so
 * what you see is what the host page gets. The code itself isn't shown: it's
 * one iframe plus one script tag, and nobody retypes that.
 */
export const EmbedModal = ({ open, onClose, tone }: Props) => {
  const [layout, setLayout] = useState<EmbedLayout>(DEFAULT_EMBED_LAYOUT);
  const [theme, setTheme] = useState<EmbedTheme>(DEFAULT_EMBED_THEME);
  const [copied, setCopied] = useState(false);
  const [origin, setOrigin] = useState('https://www.tone3000.com');

  useEffect(() => {
    setOrigin(window.location.origin);
  }, []);

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(t);
  }, [copied]);

  const toneIds = useMemo(() => (tone.id ? [tone.id] : []), [tone.id]);
  const snippet = useMemo(
    () => buildEmbedSnippet({ origin, toneIds, layout, live: true, theme, title: tone.title }),
    [origin, toneIds, layout, theme, tone.title]
  );
  // Same-origin preview; `ref` keeps it out of the share-embed numbers.
  const previewSrc = embedPlayerUrl({
    origin: '',
    toneIds,
    layout,
    live: true,
    theme,
    ref: 'preview',
  });

  const copy = () => {
    // Synchronously inside the gesture: see utils/clipboard.ts.
    void copyToClipboard(snippet).then(
      () => {
        setCopied(true);
        analytics.capture('tone.embed_copied', { layout, theme });
      },
      (error) => {
        console.error('Failed to copy embed code:', error);
        toast({
          title: 'Failed to copy',
          description: 'Your browser blocked the clipboard. Try again from a fresh click.',
          variant: 'destructive',
        });
      }
    );
  };

  if (!open) return null;

  return (
    <>
      {/* The host script the snippet ships with; it sizes the preview frame
          too. Idempotent per window, so reopening the modal is a no-op. */}
      <Script src="/embed.js" strategy="afterInteractive" />
      <InfoModal
        open={open}
        onClose={onClose}
        title="Embed this tone"
        icon={<Code size={24} className="text-white" />}
        // Tight gutters on phones so the preview frame gets the width; the
        // player is already squeezed at ~320px.
        className="max-w-2xl p-4 sm:p-8"
        action={<></>}
        message={
          <div className="flex flex-col gap-6 text-left">
            <p className="text-zinc-400">
              Put this player on your own site. Visitors can audition the tone there, or plug in and
              play through it.{' '}
              <a
                href="/embed/docs"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:underline whitespace-nowrap"
              >
                How it works
              </a>
            </p>

            <div className="flex flex-wrap items-center gap-4 sm:gap-6">
              <Pills
                label="Player layout"
                options={EMBED_LAYOUTS}
                labels={LAYOUT_LABEL}
                value={layout}
                onChange={setLayout}
              />
              <Pills
                label="Player theme"
                options={EMBED_THEMES}
                labels={THEME_LABEL}
                value={theme}
                onChange={setTheme}
              />
            </div>

            {/* The player draws its own frame and the page behind it is
                transparent, so no wrapper box. Option changes navigate this
                one frame (new src) rather than remounting it: embed.js
                re-handshakes on load, and the frame keeps its current height
                until the new page reports instead of snapping to the
                fallback `height` first. */}
            <iframe
              data-t3k-player
              src={previewSrc}
              title="Embed preview"
              allow="autoplay; microphone; speaker-selection"
              height={EMBED_FALLBACK_HEIGHT[DEFAULT_EMBED_LAYOUT]}
              className="w-full border-0 block"
            />

            <Button
              isPrimary
              onClick={copy}
              aria-label="Copy embed code"
              className="w-full sm:w-auto sm:self-start"
            >
              {copied ? <Check size={20} /> : <Copy size={20} />}
              {copied ? 'Copied' : 'Copy embed code'}
            </Button>
          </div>
        }
      />
    </>
  );
};
